import { FC, useState } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { DeleteDialogProps } from './Dialogs.d'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import { useToast } from '../toast/hooks/useToast'
import { Button, Form } from 'react-bootstrap'
import { Dialog } from 'primereact/dialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCircleNotch,
    faQuestionCircle,
    faTrash,
} from '@fortawesome/free-solid-svg-icons'

const DeleteDialog: FC<DeleteDialogProps> = ({
    record,
    message,
    url,
    showIcon = true,
    confirmText = '',
    btnLabel = '',
    doDelete,
    deletedCallback,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
    const axiosPrivate = useAxiosPrivate()
    const { triggerToast } = useToast()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [confirmTextValue, setConfirmTextValue] = useState('')
    const [confirmTextErrorClass, setConfirmTextErrorClass] = useState('')

    const deleteRecord = async (id: number) => {
        if (url) {
            setIsSubmitting(true)

            try {
                const response = await axiosPrivate.post(url).finally(() => {
                    setIsSubmitting(false)
                })
                if (response.data?.success === true) {
                    triggerToast({
                        severity: 'success',
                        summary: 'Successful',
                        detail: response.data?.data.msg,
                    })
                    setDeleteDialogVisible(false)
                    deletedCallback(response)
                } else {
                    triggerToast({
                        severity: 'error',
                        summary: 'Failed',
                        detail: response.data?.errors.join('<br />'),
                    })
                }
            } catch (err) {
                // console.log(err.response.data.errors)
                let errorMsg = ''
                // TODO: Sort out error handling!
                if (!err?.response) {
                    errorMsg = 'No Server Response'
                } else {
                    if (err.response.data?.errors) {
                        errorMsg = err.response.data.errors.join('<br />')
                    } else {
                        errorMsg = 'Delete Failed'
                    }
                }

                triggerToast({
                    severity: 'error',
                    summary: 'Failed',
                    detail: errorMsg,
                })
            }
        } else {
            doDelete()
        }
    }

    const footerContent = (
        <div>
            <Button
                variant={theme === 'dark' ? 'dark' : 'light'}
                onClick={() => setDeleteDialogVisible(false)}
                className="p-button-text"
            >
                No
            </Button>
            <Button
                variant="danger"
                onClick={() => {
                    if (confirmText && confirmText !== confirmTextValue) {
                        setConfirmTextErrorClass('is-invalid')
                        triggerToast({
                            severity: 'error',
                            summary: 'Failed',
                            detail: `You must enter ${confirmText} to continue!`,
                        })
                    } else {
                        deleteRecord(record.id)
                    }
                }}
            >
                <span className="text-white">
                    {isSubmitting ? (
                        <>
                            <FontAwesomeIcon icon={faCircleNotch} spin={true} />{' '}
                            Deleting...
                        </>
                    ) : (
                        'Yes'
                    )}{' '}
                </span>
            </Button>
        </div>
    )

    return (
        <>
            <Button
                variant="outline-danger"
                onClick={() => setDeleteDialogVisible(true)}
            >
                {showIcon && <FontAwesomeIcon icon={faTrash} />}
                {btnLabel && (
                    <span className={showIcon ? 'ms-2' : ''}>{btnLabel}</span>
                )}
            </Button>
            <Dialog
                header={
                    <span>
                        <FontAwesomeIcon icon={faQuestionCircle} /> Confirm?
                    </span>
                }
                visible={deleteDialogVisible}
                breakpoints={{ '960px': '60vw', '640px': '80vw' }}
                style={{ width: '38vw' }}
                onHide={() => setDeleteDialogVisible(false)}
                footer={footerContent}
                closable={false}
            >
                {message}
                {confirmText ? (
                    <div className="mt-5 border-1">
                        <h6>
                            To confirm, please enter the word{' '}
                            <strong className="text-danger">
                                {confirmText}
                            </strong>{' '}
                            below:
                        </h6>
                        <Form.Group className="mb-3 sfy-field shadow">
                            <Form.Control
                                type="text"
                                value={confirmTextValue}
                                className={confirmTextErrorClass}
                                onChange={(e) =>
                                    setConfirmTextValue(e.target.value)
                                }
                            />
                        </Form.Group>
                    </div>
                ) : (
                    <></>
                )}
            </Dialog>
        </>
    )
}

export default DeleteDialog
